/** The language picker ("Pick your corner.", designs/Caged Home): the 39 launch languages. */
import { useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { applyLanguage, isTranslated } from '../../i18n';
import { LANGUAGES } from '../../i18n/languages';
import { useApp } from '../../state/store';
import { Modal } from './Modal';
import s from './LanguageSheet.module.css';
import ui from './ui.module.css';

export function languageOf(code: string) {
  return LANGUAGES.find((l) => l.code === code) ?? LANGUAGES[0]!;
}

export function LanguageSheet({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const current = useApp((st) => st.settings.language);
  const updateSettings = useApp((st) => st.updateSettings);
  const titleId = useId();
  const currentRef = useRef<HTMLButtonElement>(null);

  const pick = (code: string) => {
    updateSettings({ language: code });
    void applyLanguage(code);
    onClose();
  };

  return (
    <Modal onClose={onClose} labelledBy={titleId} variant="sheet" initialFocus={currentRef}>
      <div className={s.head}>
        <div className={s.headText}>
          <span className={ui.label}>{t('screens.language.label', { count: LANGUAGES.length })}</span>
          <span id={titleId} className={s.title}>
            {t('screens.language.title')}
          </span>
        </div>
        <button type="button" className={ui.iconBtn} onClick={onClose} aria-label={t('common.close')}>
          ×
        </button>
      </div>
      <ul className={s.list}>
        {LANGUAGES.map((l) => {
          const on = l.code === current;
          return (
            <li key={l.code}>
              <button
                ref={on ? currentRef : undefined}
                type="button"
                lang={l.code}
                className={[s.option, on ? s.on : ''].join(' ')}
                aria-current={on ? 'true' : undefined}
                onClick={() => pick(l.code)}
              >
                <span className={s.names}>
                  <span className={s.native}>{l.native}</span>
                  <span className={s.english} lang="en">
                    {l.english}
                    {on && !isTranslated(l.code) && <span className={s.fallback}> · {t('screens.language.englishForNow')}</span>}
                  </span>
                </span>
                <span className={s.radio} aria-hidden="true">
                  {on ? '✓' : ''}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}
